import { afterNextRender } from './timing';

export type DateParts = Record<string, string>;
export type Pattern = string[];
export type Blocks = number[];
export interface AutoFormatOptions {
  template: string;
  pattern?: Pattern;
  value?: string;
  delimiter?: string;
}

interface ICompleteDate {
  [DAY]: number;
  [MONTH]: number;
  [YEAR]: number;
}

export interface FormatOptions {
  blocks: Blocks;
  cursorPosition: number;
  pattern: Pattern;
  prevValue: string;
  value: string;
  delimiter: string;
  prevDate: DateParts;
}

export interface BaseDateOptions {
  pattern: Pattern;
  parts: DateParts;
}

const DAY = 'd';
const MONTH = 'm';
const YEAR = 'y';
export const DEFAULT_TEMPLATE: string = 'mm/dd';

export function parseISODate(date: string): DateParts {
  if (Number.isNaN(Date.parse(date)) || date.length <= 5) {
    // append year to make date valid and a leap year
    date = `${date}-2000`;
  }

  // using GMT to make sure its UTC date remains consistent
  // this should be OK as long as we don't care about timezones
  const dateObject = new Date(date.includes('T') ? date : `${date} GMT`);

  return {
    y: dateObject.getUTCFullYear().toString(),
    m: `${dateObject.getUTCMonth() + 1}`.padStart(2, '0'),
    d: `${dateObject.getUTCDate()}`.padStart(2, '0'),
  };
}

export function toDefaultDateString(month: string, day: string) {
  return month || day ? `${month}/${day}` : '';
}

export function toStrippedDateString({ pattern, parts }: BaseDateOptions) {
  return pattern.map((part) => parts[part]).join(',');
}

export function defaultAutoFormatOptions(
  options: AutoFormatOptions,
  defaultTemplate: string,
) {
  const template =
    options.template && options.template.length > 0
      ? options.template.toLowerCase()
      : defaultTemplate;
  const value = options.value ?? '';
  const delimiter = [...template].find((part) =>
    /^[^A-Za-z]+$/.test(part),
  ) as string;
  const pattern = template.split(delimiter).map((part) => part[0]);

  return {
    template,
    delimiter,
    pattern,
    value,
  };
}

export function setCursorPosition(element: HTMLInputElement, position: number) {
  afterNextRender(() => element.setSelectionRange(position, position));
}

function isFullDate(
  { value, delimiter }: FormatOptions,
  template: string,
  delimiters: string,
) {
  return (
    getCharCount(delimiter, value) == 2 ||
    (getDelimiterCountByArray(value, delimiters, 2) &&
      getCharCount(delimiter, template) == 1)
  );
}

function replaceDelimiters(
  value: string,
  delimiters: string,
  delimiter: string,
) {
  const regExp = new RegExp(`[${delimiters}]`, 'g');

  return value.replaceAll(regExp, delimiter);
}

function getDelimiters(string: string) {
  const regExp = new RegExp(`[a-zA-Z0-9]`, 'g');

  return string.replaceAll(regExp, '');
}

function getDelimiterCountByArray(
  value: string,
  delimiters: string,
  qty: number,
) {
  return [...delimiters].some(
    (delimiter) => getCharCount(delimiter, value) == qty,
  );
}

function escapeSpecialChar(string: string) {
  return string.replaceAll(/[$()*+.?[\\\]^{|}]/g, '\\$&');
}

function getCharCount(char: string, string: string) {
  const escapedChar = escapeSpecialChar(char);
  return (string.match(new RegExp(escapedChar, 'g')) || []).length;
}

function stringToInt(value: string) {
  return Number.parseInt(value, 10);
}

function getFormattedDateParts(options: FormatOptions) {
  const { blocks, cursorPosition, pattern, value, delimiter } = options;
  let currentBlock: string = pattern[0];
  const chars = preFormatValue(value, currentBlock);
  let cursor = cursorPosition;
  const dateParts = pattern.reduce((accumulator, part) => {
    accumulator[part] = '';
    return accumulator;
  }, {} as DateParts);

  for (let index = 0; index < chars.length && currentBlock; index += 1) {
    if (chars[index] !== delimiter) {
      dateParts[currentBlock] += chars[index];
    }

    const formatted = formatDateBlock(
      currentBlock,
      dateParts[currentBlock],
      chars[index] === delimiter && cursorPosition > index,
    );

    cursor += getCursorOffset(
      cursorPosition,
      index,
      formatted.length - dateParts[currentBlock].length,
    );

    dateParts[currentBlock] = formatted;

    if (
      isDatePartComplete(
        dateParts[currentBlock],
        currentBlock,
        blocks,
        pattern,
      ) ||
      isBlockEndedByDelimiter(
        chars[index],
        dateParts[currentBlock],
        getNextBlock(currentBlock, pattern),
        delimiter,
      )
    ) {
      currentBlock = getNextBlock(currentBlock, pattern);
    }
  }

  return {
    formattedDateParts: dateParts,
    cursor,
  };
}

function getCursorOffset(cursor: number, index: number, value: number): number {
  if (cursor > index) {
    return value;
  }

  return 0;
}

function isDatePartComplete(
  datePart: string,
  block: string,
  blocks: Blocks,
  pattern: Pattern,
): boolean {
  return datePart.length === blocks[pattern.indexOf(block)];
}

function isBlockEndedByDelimiter(
  currentChar: string,
  datePart: string,
  nextBlock: string,
  delimiter: string,
) {
  return currentChar === delimiter && datePart.length > 0 && nextBlock;
}

function getNextBlock(current: string, pattern: Pattern): string {
  return pattern[pattern.indexOf(current) + 1];
}

function formatDateBlock(
  blockId: string,
  value: string,
  complete?: boolean,
): string {
  const cleanValue = getSanitizedNumericString(value);

  if (!cleanValue) {
    return cleanValue;
  }

  switch (blockId) {
    case MONTH:
      return formatMonth(cleanValue, complete);

    case DAY:
      return formatDay(cleanValue, complete);

    default:
      return cleanValue;
  }
}

function formatDay(value: string, complete?: boolean): string {
  const numericValue = Number.parseInt(value, 10);

  if (value.length === 1) {
    if (complete || numericValue * 10 > 31) {
      return `0${Math.max(numericValue, 1)}`;
    }
  } else if (numericValue > 31) {
    return '31';
  } else if (numericValue < 1) {
    return '01';
  }

  return value;
}

function preFormatValue(value: string, type: string) {
  const chars = [...value];

  if (chars.length >= 2 && chars.length <= 3) {
    const part = value.slice(0, 2);
    const extra = chars.length <= 3 ? value[2] : '';
    const newValue: string =
      (type == MONTH && !isValidMonth(part)) ||
      (type == DAY && !isValidDay(part))
        ? `0${part}${extra}`
        : value;

    return [...newValue];
  }

  return chars;
}

function isValidMonth(month: string) {
  const m = Number.parseInt(month);

  return m >= 1 && m <= 12;
}

function isValidDay(day: string) {
  const d = Number.parseInt(day);

  return d >= 1 && d <= 31;
}

function formatMonth(value: string, complete?: boolean): string {
  const numericValue = Number.parseInt(value, 10);

  if (value.length === 1) {
    if (complete || numericValue * 10 > 12) {
      return `0${Math.max(numericValue, 1)}`;
    }
  } else if (numericValue > 12) {
    return '12';
  } else if (numericValue < 1) {
    return '01';
  }

  return value;
}

function getSanitizedNumericString(value: string): string {
  return value.replaceAll(/\D/g, '');
}

function combineDateParts(
  dateParts: DateParts,
  pattern: Pattern,
  delimiter: string,
): string {
  const blocks = getBlocks(pattern);

  return pattern
    .map((block) => {
      const nextBlock = getNextBlock(block, pattern);
      const blockIndex = pattern.indexOf(block);
      const insertDelimiter =
        nextBlock &&
        (dateParts[nextBlock] ||
          dateParts[block]?.length === blocks[blockIndex]);

      return insertDelimiter ? dateParts[block] + delimiter : dateParts[block];
    })
    .join('');
}

export function getBlocks(pattern: Pattern): Blocks {
  const blocks: Blocks = [];

  for (const value of pattern) {
    if (value === YEAR) {
      blocks.push(4);
    } else {
      blocks.push(2);
    }
  }

  return blocks;
}

function getTemplate(
  pattern: Pattern,
  blocks: Blocks,
  delimiter: string,
): string {
  return pattern
    .map((letter, index) => {
      const count = blocks[index];
      return letter.repeat(count).toUpperCase();
    })
    .join(delimiter);
}

function getCorrectedDateParts(dateParts: DateParts): DateParts {
  const correctedDay = correctDayForMonth({
    [DAY]: stringToInt(dateParts[DAY]),
    [MONTH]: stringToInt(dateParts[MONTH]),
    [YEAR]: stringToInt(dateParts[YEAR]),
  });

  return {
    [DAY]: formatDay(correctedDay.toString(), true),
    [MONTH]: dateParts[MONTH],
    [YEAR]: dateParts[YEAR],
  };
}

function correctDayForMonth({ d, m, y }: ICompleteDate) {
  const day = Math.min(d, 31);
  const month = Math.min(m, 12);
  const year = Number.parseInt(String(y || 0), 10);

  if ((month < 7 && month % 2 === 0) || (month > 8 && month % 2 === 1)) {
    const leapDay = isLeapYear(year) ? 29 : 28;
    return Math.min(day, month === 2 ? leapDay : 30);
  }

  return day;
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function formatDateInput(options: FormatOptions) {
  const {
    blocks,
    cursorPosition,
    pattern,
    prevValue,
    prevDate,
    value,
    delimiter,
  } = options;
  const template = getTemplate(pattern, blocks, delimiter);
  const delimiters = getDelimiters(value);

  if (isFullDate(options, template, delimiters)) {
    options.value = replaceDelimiters(value, delimiters, delimiter);
  } else if (prevValue?.length > value.length) {
    const { formattedDateParts } = getFormattedDateParts(options);
    const date =
      Object.values(formattedDateParts).join(delimiter).length ===
      template.length
        ? getCorrectedDateParts(formattedDateParts)
        : formattedDateParts;
    return {
      formattedDate: replaceDelimiters(value, delimiters, delimiter),
      cursorPosition,
      date,
    };
  } else if (value.length > template.length) {
    return {
      formattedDate: prevValue,
      cursorPosition: cursorPosition - (value.length - prevValue?.length),
      date: prevDate,
    };
  }

  const { formattedDateParts, cursor } = getFormattedDateParts(options);
  if (
    Object.values(formattedDateParts).join(delimiter).length === template.length
  ) {
    const correctedDateParts = getCorrectedDateParts(formattedDateParts);

    return {
      formattedDate: combineDateParts(correctedDateParts, pattern, delimiter),
      cursorPosition: cursor,
      date: correctedDateParts,
    };
  }

  return {
    formattedDate: combineDateParts(formattedDateParts, pattern, delimiter),
    cursorPosition: cursor,
    date: formattedDateParts,
  };
}
